import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelectedBrand } from '../hooks/useApi';
import LoadingSpinner from '../components/LoadingSpinner';
import apiService from '../services/api';

const StatusBadge = ({ status }) => {
  const styles = {
    on_track: 'bg-green-100 text-green-800',
    behind: 'bg-red-100 text-red-800',
    no_target: 'bg-gray-100 text-gray-600',
  };
  const labels = { on_track: 'On Track', behind: 'Behind', no_target: 'No Target' };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || styles.no_target}`}>
      {labels[status] || status}
    </span>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { selectedBrandId, setSelectedBrandId, brands: brandOptions, brandsLoading } = useSelectedBrand();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    if (!selectedBrandId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getDashboard(selectedBrandId);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBrandId]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (brandsLoading) return <LoadingSpinner />;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Command Center</h1>
          <p className="text-sm text-gray-500 mt-1">Everything at a glance</p>
        </div>
        {brandOptions.length > 1 && (
          <select
            value={selectedBrandId || ''}
            onChange={e => setSelectedBrandId(Number(e.target.value))}
            className="border border-gray-300 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500"
          >
            {brandOptions.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{error}</div>
      )}

      {loading ? <LoadingSpinner /> : data && (
        <>
          {/* Top Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* This Week */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">This Week</h3>
                <StatusBadge status={data.this_week.status} />
              </div>
              <div className="text-3xl font-bold text-gray-900">
                {data.this_week.published}<span className="text-lg text-gray-400">/{data.this_week.target}</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">posts published</p>
            </div>

            {/* Pipeline */}
            <div
              className="bg-white rounded-2xl border border-gray-200 p-6 cursor-pointer hover:border-accent-300 transition-colors"
              onClick={() => navigate('/pipeline')}
            >
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Pipeline</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Drafts</span>
                  <span className="text-sm font-semibold text-amber-600">{data.pipeline.drafts}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Scheduled</span>
                  <span className="text-sm font-semibold text-blue-600">{data.pipeline.scheduled}</span>
                </div>
              </div>
            </div>

            {/* Audience */}
            <div
              className="bg-white rounded-2xl border border-gray-200 p-6 cursor-pointer hover:border-accent-300 transition-colors"
              onClick={() => navigate('/audience')}
            >
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Audience</h3>
              <div className="text-3xl font-bold text-gray-900">{data.audience.followers.toLocaleString()}</div>
              <p className="text-sm text-gray-500 mt-1">
                {data.audience.follower_growth >= 0 ? '+' : ''}{data.audience.follower_growth} this month
              </p>
              {data.audience.connections > 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  ICP: {data.audience.icp_pct}% of {data.audience.connections} connections
                </p>
              )}
            </div>
          </div>

          {/* Last Posts Performance */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Last 5 Posts — Performance</h3>
              <button
                onClick={() => navigate('/performance')}
                className="text-sm text-accent-600 hover:text-accent-800 font-medium"
              >
                View all
              </button>
            </div>

            {data.last_posts.length === 0 ? (
              <p className="text-gray-400 text-sm">No published posts yet</p>
            ) : (
              <div className="space-y-3">
                {data.last_posts.map(post => (
                  <div key={post.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div className="flex-1 min-w-0 mr-4">
                      <p className="text-sm text-gray-800 truncate">{post.content_preview}</p>
                      {post.post_type && (
                        <span className="text-xs text-gray-400">{post.post_type}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 flex-shrink-0">
                      <span title="Impressions">
                        <i className="fas fa-eye mr-1"></i>{post.impressions.toLocaleString()}
                      </span>
                      <span title="Likes">
                        <i className="fas fa-thumbs-up mr-1"></i>{post.likes}
                      </span>
                      <span title="Comments">
                        <i className="fas fa-comment mr-1"></i>{post.comments}
                      </span>
                      {post.impressions === 0 && post.likes === 0 && (
                        <span className="text-red-400" title="No engagement data">
                          <i className="fas fa-exclamation-circle"></i>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {data.last_posts.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-100 flex gap-6 text-sm text-gray-500">
                <span>Avg impressions: <strong>{data.avg_impressions.toLocaleString()}</strong></span>
                <span>Avg engagement: <strong>{data.avg_engagement_rate}%</strong></span>
              </div>
            )}
          </div>

          {/* Cadence */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Cadence</h3>
              <button
                onClick={() => navigate('/calendar')}
                className="text-sm text-accent-600 hover:text-accent-800 font-medium"
              >
                Calendar
              </button>
            </div>
            <div className="flex gap-3">
              {data.cadence_weeks.map((week, i) => {
                const pct = week.target > 0 ? Math.round(week.hits / week.target * 100) : 0;
                const isCurrent = i === data.cadence_weeks.length - 1;
                return (
                  <div key={i} className="flex-1">
                    <div className="text-xs text-gray-400 mb-1">
                      {isCurrent ? 'This week' : `Week ${i + 1}`}
                    </div>
                    <div className="flex gap-1">
                      {Array.from({ length: week.target || 1 }).map((_, j) => (
                        <div
                          key={j}
                          className={`h-6 flex-1 rounded ${
                            j < week.hits ? 'bg-green-400' : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{week.hits}/{week.target}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
